// Copyright 2018 Sorama B.V.

import { ArrayExtensions } from "extensions/ArrayExtensions";
import { CSVHeader, CSVWriter } from "libraries/CSV/CSVWriter";
import { Unit } from "units/Unit";
import { DataPoint, IDataExporter, Series, Set } from "./IDataExporter";

export class CSVDataExporter implements IDataExporter {
    // Set a maximum of 10000000 iterations
    private readonly maxIterations: number = 10000000;

    public exportSets(sets: Set[]): string {
        const headers: CSVHeader[] = [];
        let maxLength = 0;
        for (let i = 0; i < sets.length; i++) {
            const set = sets[i];
            headers.push({
                key: i.toFixed(0),
                label: this.generateLabel(set),
            });

            maxLength = Math.max(maxLength, set.data.length);
        }

        const writer = new CSVWriter(headers);
        for (let i = 0; i < maxLength; i++) {
            const line: { [key: string]: number | string } = {};

            for (let j = 0; j < sets.length; j++) {
                const set = sets[j];

                if (i < set.data.length) {
                    line[j] = set.data[i];
                }
            }

            writer.writeLine(line);
        }

        return writer.getCSV();
    }

    public exportSeries(xUnit: Unit, series: Series[]): string {
        const columnHeaders = series.map((x, i) => <CSVHeader>{ key: i, label: this.generateLabel(x) });
        columnHeaders.splice(0, 0, {
            key: "x",
            label: xUnit.toUnitType() + " [" + xUnit.unit + "]",
        });

        // Create a CSV writer
        const writer = new CSVWriter(columnHeaders);

        // Generate the total xaxis array
        let xAxis = <number[]>[];

        // Initialize xAxis array with the smallest value
        let x = Number.POSITIVE_INFINITY;
        for (const s of series) {
            x = Math.min(s.data[0].x, x);
        }

        xAxis.push(x);
        const indices = <number[]>[];
        for (let i = 0; i < series.length; i++) {
            indices[i] = Math.abs(series[i].data[0].x - x) < 1e-8 ? 1 : 0;
        }

        let tol = 0.0;

        // Now find all unique x values
        const tolerance = 1e-5;

        for (let count = 0; count < this.maxIterations; count++) {
            // Find the next biggest value (with tolerance in mind)
            let xx = Number.POSITIVE_INFINITY;

            for (let i = 0; i < series.length; i++) {
                const s = series[i];
                let j = indices[i];
                for (; j < s.data.length; j++) {
                    if (s.data[j].x > x + tolerance) {
                        xx = Math.min(xx, s.data[j].x);
                        indices[i] = j;
                        break;
                    }
                }

                indices[i] = j;
            }

            if (xx === Number.POSITIVE_INFINITY) {
                break;
            }

            xAxis.push(xx);
            x = xx;
        }

        xAxis = xAxis.filter((x) => Number.isFinite(x));
        tol = tol / xAxis.length;

        // Write values
        for (let i = 0; i < xAxis.length; i++) {
            // Generate an object containing all values
            const line = <{ [key: string]: string | number }>{};
            const x = xAxis[i];
            line["x"] = x;

            for (let j = 0; j < series.length; j++) {
                const s = series[j];
                const index = ArrayExtensions.fastIndexOf<DataPoint>(s.data, (v) => {
                    if (Math.abs(v.x - x) < tolerance) return 0;

                    if (v.x < x) return 1;
                    else return -1;
                });

                if (index !== -1 && Math.abs(x - s.data[index].x) <= tolerance) {
                    const y = s.data[index].y;
                    line[j] = y;
                }
            }

            writer.writeLine(line);
        }

        return writer.getCSV();
    }

    private generateLabel(setOrSeries: Set | Series): string {
        return (setOrSeries.label ? setOrSeries.label : "") + " [" + setOrSeries.yUnit.unit + "]";
    }
}
